import api from './api';

export const adminService = {
  // ============ COMPONENTS ============

  /**
   * Get all components by type (bases, shades, colors)
   * @param {string} type - 'bases', 'shades', or 'colors'
   */
  getComponents: async (type) => {
    const response = await api.get(`/admin/components/${type}`);
    return response.data;
  },

  /**
   * Create a new component
   * @param {string} type - 'bases', 'shades', or 'colors'
   * @param {object} componentData - Component data
   */
  createComponent: async (type, componentData) => {
    const response = await api.post(`/admin/components/${type}`, componentData);
    return response.data;
  },

  /**
   * Update an existing component
   * @param {string} componentId - UUID of the component
   * @param {object} componentData - Updated component data
   */
  updateComponent: async (componentId, componentData) => {
    const response = await api.put(`/admin/components/${componentId}`, componentData);
    return response.data;
  },

  /**
   * Soft delete a component (sets isActive = false)
   * @param {string} componentId - UUID of the component
   */
  deleteComponent: async (componentId) => {
    const response = await api.delete(`/admin/components/${componentId}`);
    return response.data;
  },

  /**
   * Restore a deleted component (sets isActive = true)
   * @param {string} componentId - UUID of the component
   */
  restoreComponent: async (componentId) => {
    const response = await api.put(`/admin/components/${componentId}/restore`);
    return response.data;
  },

  // ============ ORDERS ============

  /**
   * Get all orders with pagination
   * @param {number} page - Page number (0-indexed)
   * @param {number} size - Page size
   * @param {string|null} status - Optional status filter
   */
  getOrders: async (page = 0, size = 20, status = null) => {
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (status && status !== 'ALL') {
      params.append('status', status);
    }
    const response = await api.get(`/admin/orders?${params}`);
    return response.data;
  },

  /**
   * Get order by ID
   * @param {string} orderId - UUID of the order
   */
  getOrderById: async (orderId) => {
    const response = await api.get(`/admin/orders/${orderId}`);
    return response.data;
  },

  /**
   * Update order status
   * @param {string} orderId - UUID of the order
   * @param {object} statusData - { status, trackingNumber?, deliveryPartner?, trackingUrl?, notes? }
   */
  updateOrderStatus: async (orderId, statusData) => {
    const response = await api.put(`/admin/orders/${orderId}/status`, statusData);
    return response.data;
  },

  /**
   * Get order statistics
   */
  getOrderStats: async () => {
    const response = await api.get('/admin/orders/stats');
    return response.data;
  },

  // ============ SUPPORT TICKETS ============

  /**
   * Get all support tickets with pagination
   * @param {number} page - Page number (0-indexed)
   * @param {number} size - Page size
   * @param {string|null} status - Optional status filter
   */
  getTickets: async (page = 0, size = 20, status = null) => {
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (status && status !== 'ALL') {
      params.append('status', status);
    }
    const response = await api.get(`/admin/support/tickets?${params}`);
    return response.data;
  },

  /**
   * Get ticket by ID with messages
   * @param {string} ticketId - UUID of the ticket
   */
  getTicketById: async (ticketId) => {
    const response = await api.get(`/admin/support/tickets/${ticketId}`);
    return response.data;
  },

  /**
   * Update ticket status/priority/assignment
   * @param {string} ticketId - UUID of the ticket
   * @param {object} updateData - { status?, priority?, assignedTo? }
   */
  updateTicket: async (ticketId, updateData) => {
    const response = await api.patch(`/admin/support/tickets/${ticketId}`, updateData);
    return response.data;
  },

  /**
   * Add admin message to ticket
   * @param {string} ticketId - UUID of the ticket
   * @param {string} message - Message content
   */
  addTicketMessage: async (ticketId, message) => {
    const response = await api.post(`/admin/support/tickets/${ticketId}/messages`, { message });
    return response.data;
  },

  /**
   * Get support ticket statistics
   */
  getTicketStats: async () => {
    const response = await api.get('/admin/support/stats');
    return response.data;
  },

  getCoupons: async (page = 0, size = 20, status = null) => {
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (status && status !== 'ALL') {
      params.append('status', status);
    }
    const response = await api.get(`/coupons/admin?${params}`);
    return response.data;
  },

  getCouponById: async (couponId) => {
    const response = await api.get(`/coupons/admin/${couponId}`);
    return response.data;
  },

  createCoupon: async (couponData) => {
    const response = await api.post('/coupons/admin', couponData);
    return response.data;
  },

  updateCoupon: async (couponId, couponData) => {
    const response = await api.put(`/coupons/admin/${couponId}`, couponData);
    return response.data;
  },

  deleteCoupon: async (couponId) => {
    const response = await api.delete(`/coupons/admin/${couponId}`);
    return response.data;
  },

  getShippingConfig: async () => {
    const response = await api.get('/admin/settings/shipping');
    return response.data;
  },

  updateShippingConfig: async (data) => {
    const response = await api.put('/admin/settings/shipping', data);
    return response.data;
  },
};
